import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';

const HTMLImageMaps = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Images & Graphics</div>
                <h1>Image Maps & Clickable Areas</h1>
                <p className="hero-lead">
                    Create interactive images with multiple clickable regions. Learn to define custom shapes
                    and create accessible, responsive image maps with HTML.
                </p>
            </div>

            <div className="content-section">
                <h2>What are Image Maps?</h2>
                <p>
                    Image maps allow you to define clickable regions (hotspots) within a single image,
                    where each region can link to different destinations.
                </p>

                <div className="use-cases">
                    <h3>Common Use Cases:</h3>
                    <ul>
                        <li>Interactive infographics</li>
                        <li>Clickable floor plans or building layouts</li>
                        <li>Geographic maps with clickable regions</li>
                        <li>Product diagrams with part identification</li>
                        <li>Anatomical charts</li>
                        <li>Navigation menus with custom shapes</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Basic Image Map Structure</h2>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- 1. Create the image with usemap attribute --&gt;
<img src="world-map.jpg" 
     alt="World map" 
     usemap="#worldmap"
     width="800" 
     height="600">

<!-- 2. Define the map with matching name --&gt;
<map name="worldmap">
    <!-- 3. Define clickable areas --&gt;
    <area shape="rect" 
          coords="0,0,200,300" 
          href="/north-america" 
          alt="North America">
    
    <area shape="circle" 
          coords="400,300,100" 
          href="/europe" 
          alt="Europe">
    
    <area shape="poly" 
          coords="200,400,300,500,250,550,150,500" 
          href="/south-america" 
          alt="South America">
</map>`} />
                </div>

                <div className="syntax-note">
                    <h3>Key Components:</h3>
                    <ul>
                        <li><code>usemap="#name"</code> - Links image to map</li>
                        <li><code>&lt;map name="name"&gt;</code> - Defines the map</li>
                        <li><code>&lt;area&gt;</code> - Defines each clickable region</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Area Shapes</h2>

                <h3>1. Rectangle (rect)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<area shape="rect" 
      coords="x1,y1,x2,y2" 
      href="link.html" 
      alt="Description">

<!-- coords format: left, top, right, bottom --&gt;
<!-- Example: Button from (10,10) to (110,50) --&gt;
<area shape="rect" 
      coords="10,10,110,50" 
      href="/button-action" 
      alt="Click here button">`} />
                </div>

                <h3>2. Circle (circle)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<area shape="circle" 
      coords="x,y,radius" 
      href="link.html" 
      alt="Description">

<!-- coords format: center-x, center-y, radius --&gt;
<!-- Example: Circle at (150,150) with radius 50 --&gt;
<area shape="circle" 
      coords="150,150,50" 
      href="/circular-region" 
      alt="Circular button">`} />
                </div>

                <h3>3. Polygon (poly)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<area shape="poly" 
      coords="x1,y1,x2,y2,x3,y3,..." 
      href="link.html" 
      alt="Description">

<!-- coords format: pairs of x,y coordinates --&gt;
<!-- Each pair is a vertex of the polygon --&gt;
<!-- Automatically closes from last to first point --&gt;

<!-- Example: Triangle --&gt;
<area shape="poly" 
      coords="100,50,150,150,50,150" 
      href="/triangle" 
      alt="Triangle region">

<!-- Example: Irregular shape (5 points) --&gt;
<area shape="poly" 
      coords="50,10,90,30,80,70,20,70,10,30" 
      href="/custom-shape" 
      alt="Custom region">`} />
                </div>

                <h3>4. Default (default)</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<area shape="default" 
      href="default-link.html" 
      alt="Default region">

<!-- Covers entire image except defined areas --&gt;
<!-- Use as fallback/catch-all region --&gt;
<!-- Should be last area in map --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Complete Example: Interactive Product Diagram</h2>
                <div className="code-example">
                    <div className="code-header">
                        <span>Laptop Diagram with Clickable Parts</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="laptop-diagram.jpg" 
     alt="Laptop parts diagram" 
     usemap="#laptop-parts"
     width="800" 
     height="600">

<map name="laptop-parts">
    <!-- Screen (rectangle) --&gt;
    <area shape="rect" 
          coords="200,50,600,350" 
          href="/parts/screen" 
          alt="Screen - 15.6 inch display"
          title="Click for screen details">
    
    <!-- Keyboard (rectangle) --&gt;
    <area shape="rect" 
          coords="200,400,600,550" 
          href="/parts/keyboard" 
          alt="Keyboard"
          title="Click for keyboard details">
    
    <!-- Trackpad (rectangle) --&gt;
    <area shape="rect" 
          coords="350,560,450,590" 
          href="/parts/trackpad" 
          alt="Trackpad"
          title="Click for trackpad details">
    
    <!-- Power button (circle) --&gt;
    <area shape="circle" 
          coords="50,100,20" 
          href="/parts/power-button" 
          alt="Power button"
          title="Power button information">
    
    <!-- USB port (small rectangle) --&gt;
    <area shape="rect" 
          coords="650,400,680,420" 
          href="/parts/usb-port" 
          alt="USB port"
          title="USB-C port information">
</map>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Accessibility</h2>
                <p>
                    Image maps must be accessible to screen readers and keyboard users.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Accessible Image Map</span>
                    </div>
                    <CodeBlock language="html" code={`<img src="navigation-map.jpg" 
     alt="Site navigation map" 
     usemap="#site-nav"
     width="1000" 
     height="400">

<map name="site-nav">
    <!-- ALWAYS include alt text for each area --&gt;
    <area shape="rect" 
          coords="0,0,250,400" 
          href="/products" 
          alt="Products section">
    
    <area shape="rect" 
          coords="250,0,500,400" 
          href="/services" 
          alt="Services section">
    
    <area shape="rect" 
          coords="500,0,750,400" 
          href="/about" 
          alt="About us section">
    
    <area shape="rect" 
          coords="750,0,1000,400" 
          href="/contact" 
          alt="Contact us section">
</map>

<!-- Best practices: --&gt;
<!-- Include alt on main image --&gt;
<!-- Include alt on each area --&gt;
<!-- Areas are keyboard-accessible (native behavior) --&gt;
<!-- Consider adding title for hover tooltips --&gt;`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Responsive Image Maps</h2>
                <p>
                    Traditional image maps use fixed pixel coordinates, which break on different screen sizes.
                    Solutions include JavaScript libraries or CSS-based alternatives.
                </p>

                <h3>Problem:</h3>
                <div className="code-example error">
                    <CodeBlock language="html" code={`<!-- Fixed coordinates don't scale! --&gt;
<img src="map.jpg" 
     alt="Map" 
     usemap="#mymap"
     style="width: 100%">  <!-- Image scales --&gt;

<map name="mymap">
    <area coords="100,100,200,200" ... />  <!-- Coords don't! --&gt;
</map>`} />
                </div>

                <h3>Solution 1: JavaScript Library</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- Use ImageMapResizer library --&gt;
<script src="imageMapResizer.min.js"></script>

<img src="map.jpg" 
     alt="Map" 
     usemap="#mymap"
     style="width: 100%">

<map name="mymap">
    <area shape="rect" coords="100,100,200,200" href="..." alt="...">
</map>

<script>
    imageMapResize(); // Auto-scales coordinates
</script>`} />
                </div>

                <h3>Solution 2: SVG Alternative</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<!-- SVG scales naturally --&gt;
<svg viewBox="0 0 800 600" style="width: 100%;">
    <image href="map.jpg" width="800" height="600" />
    
    <!-- Clickable regions --&gt;
    <a href="/region-1">
        <rect x="100" y="100" width="100" height="100" 
              fill="transparent" />
        <title>Region 1</title>
    </a>
    
    <a href="/region-2">
        <circle cx="400" cy="300" r="50" 
                fill="transparent" />
        <title>Region 2</title>
    </a>
</svg>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Creating Image Maps</h2>

                <h3>Manual Method:</h3>
                <ol>
                    <li>Open image in image editor</li>
                    <li>Note pixel coordinates of regions</li>
                    <li>Write HTML with those coordinates</li>
                </ol>

                <h3>Tools (Easier!):</h3>
                <ul>
                    <li><strong>Image Map Generator:</strong> image-map.net</li>
                    <li><strong>Mapedit:</strong> Desktop application</li>
                    <li><strong>Photoshop/GIMP:</strong> Can export image maps</li>
                    <li><strong>Online tools:</strong> Draw shapes, get HTML code</li>
                </ul>

                <div className="tool-workflow">
                    <h4>Typical Tool Workflow:</h4>
                    <ol>
                        <li>Upload your image</li>
                        <li>Draw shapes on clickable regions</li>
                        <li>Add URLs and alt text</li>
                        <li>Tool generates HTML code</li>
                        <li>Copy/paste into your page</li>
                    </ol>
                </div>
            </div>

            <div className="content-section">
                <h2>Advanced Techniques</h2>

                <h3>1. Overlapping Areas</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<map name="overlap">
    <!-- First area in code has priority --&gt;
    <area shape="circle" coords="150,150,50" href="/circle" alt="Circle">
    <area shape="rect" coords="100,100,200,200" href="/rect" alt="Rectangle">
    
    <!-- Where they overlap, circle link takes precedence --&gt;
</map>`} />
                </div>

                <h3>2. JavaScript Enhancement</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<img src="map.jpg" alt="Interactive map" usemap="#mymap" id="my-image">

<map name="mymap">
    <area id="region-1" shape="rect" coords="0,0,200,200" 
          href="/region-1" alt="Region 1">
    <area id="region-2" shape="rect" coords="200,0,400,200" 
          href="/region-2" alt="Region 2">
</map>

<script>
// Add hover effects
document.querySelectorAll('area').forEach(area => {
    area.addEventListener('mouseenter', (e) => {
        console.log('Hovered:', e.target.alt);
        // Could highlight region, show tooltip, etc.
    });
    
    area.addEventListener('click', (e) => {
        if (someCondition) {
            e.preventDefault(); // Prevent navigation if needed
            // Handle click with JavaScript instead
        }
    });
});
</script>`} />
                </div>

                <h3>3. Target Attribute</h3>
                <div className="code-example">
                    <CodeBlock language="html" code={`<map name="mymap">
    <!-- Open in new tab --&gt;
    <area shape="rect" 
          coords="0,0,100,100" 
          href="https://external-site.com" 
          target="_blank"
          rel="noopener noreferrer"
          alt="External link">
    
    <!-- Open in iframe --&gt;
    <area shape="rect" 
          coords="100,0,200,100" 
          href="/content.html" 
          target="content-frame"
          alt="Load in iframe">
</map>

<iframe name="content-frame" width="800" height="600"></iframe>`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Best Practices</h2>
                <div className="best-practices-grid">
                    <div className="practice-card">
                        <h3>Do</h3>
                        <ul>
                            <li>Always include alt text on image and all areas</li>
                            <li>Specify width and height on image</li>
                            <li>Use descriptive alt text for each region</li>
                            <li>Test keyboard navigation (Tab key)</li>
                            <li>Consider mobile responsiveness</li>
                            <li>Add title attributes for hover tooltips</li>
                            <li>Use image map tools (don't calculate manually)</li>
                            <li>Provide text alternative for critical navigation</li>
                        </ul>
                    </div>
                    <div className="practice-card error">
                        <h3> Don't</h3>
                        <ul>
                            <li>Forget alt text (inaccessible)</li>
                            <li>Use for essential navigation only</li>
                            <li>Create tiny clickable areas (&lt; 4444px)</li>
                            <li>Rely on image maps for mobile sites</li>
                            <li>Use without fallback on mobile</li>
                            <li>Make coordinates calculation errors</li>
                            <li>Forget to test with keyboard</li>
                            <li>Use for complex interactions (use JavaScript)</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <h2>Modern Alternatives</h2>
                <p>
                    Consider these alternatives for modern web development:
                </p>

                <div className="alternatives">
                    <h3>1. CSS + Absolute Positioning</h3>
                    <div className="code-example">
                        <CodeBlock language="html" code={`<div class="interactive-image">
    <img src="diagram.jpg" alt="Diagram">
    <a href="/part-1" class="hotspot" style="top: 10%; left: 10%; width: 20%; height: 15%;">
        <span class="sr-only">Part 1</span>
    </a>
    <a href="/part-2" class="hotspot" style="top: 30%; left: 40%; width: 15%; height: 20%;">
        <span class="sr-only">Part 2</span>
    </a>
</div>

<style>
.interactive-image {
    position: relative;
    display: inline-block;
}
.hotspot {
    position: absolute;
    display: block;
    border: 2px dashed transparent;
}
.hotspot:hover,
.hotspot:focus {
    border-color: blue;
    background: rgba(0,0,255,0.1);
}
</style>`} />
                    </div>

                    <h3>2. SVG (Best for Scalability)</h3>
                    <p>Use inline SVG with clickable shapes (shown earlier)</p>

                    <h3>3. Canvas + JavaScript</h3>
                    <p>For complex interactive graphics with animations</p>

                    <h3>4. CSS Grid/Flexbox</h3>
                    <p>For rectangular regions, use CSS grid overlay</p>
                </div>
            </div>

            <div className="content-section">
                <h2>When to Use Image Maps</h2>
                <div className="when-to-use">
                    <h3>Good Use Cases:</h3>
                    <ul>
                        <li>Desktop-only applications</li>
                        <li>Simple clickable regions</li>
                        <li>Static content</li>
                        <li>Quick prototypes</li>
                        <li>Legacy browser support needed</li>
                    </ul>

                    <h3> Avoid For:</h3>
                    <ul>
                        <li>Mobile-responsive sites</li>
                        <li>Complex interactions</li>
                        <li>Dynamic content</li>
                        <li>Hover effects/animations</li>
                        <li>Primary navigation</li>
                    </ul>
                </div>
            </div>

        </div>
    );
};

export default HTMLImageMaps;




